// utils/localStorage.ts
class LocalStorageHelper {
    private static fallbackStorage: { [key: string]: string } = {};
    private static isLocalStorageAvailable: boolean | null = null;

    private static checkLocalStorageAvailability(): boolean {
        if (this.isLocalStorageAvailable !== null) {
            return this.isLocalStorageAvailable;
        }

        try {
            // Check if we're in a browser environment
            if (typeof window === 'undefined') {
                this.isLocalStorageAvailable = false;
                return false;
            }

            const test = '__localStorage_test__';
            localStorage.setItem(test, 'test');
            localStorage.removeItem(test);
            this.isLocalStorageAvailable = true;
            return true;
        } catch (error) {
            console.warn('localStorage is not available, using fallback storage:', error);
            this.isLocalStorageAvailable = false;
            return false;
        }
    }

    static getItem(key: string): string | null {
        if (this.checkLocalStorageAvailability()) {
            try {
                return localStorage.getItem(key);
            } catch (error) {
                console.warn(`localStorage.getItem failed for key "${key}":`, error);
            }
        }
        
        return this.fallbackStorage[key] || null;
    }

    static setItem(key: string, value: string): void {
        if (this.checkLocalStorageAvailability()) {
            try {
                localStorage.setItem(key, value);
                // Also store in fallback for consistency
                this.fallbackStorage[key] = value;
                return;
            } catch (error) {
                console.warn(`localStorage.setItem failed for key "${key}":`, error);
            }
        }
        
        // Use fallback storage
        this.fallbackStorage[key] = value;
    }

    static removeItem(key: string): void {
        if (this.checkLocalStorageAvailability()) {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                console.warn(`localStorage.removeItem failed for key "${key}":`, error);
            }
        }
        
        delete this.fallbackStorage[key];
    }

    static clear(): void {
        if (this.checkLocalStorageAvailability()) {
            try {
                localStorage.clear();
            } catch (error) {
                console.warn('localStorage.clear failed:', error);
            }
        }
        
        this.fallbackStorage = {};
    }

    // Method to check if localStorage is working
    static isAvailable(): boolean {
        return this.checkLocalStorageAvailability();
    }

    // Method to get info about storage type being used
    static getStorageInfo(): { type: 'localStorage' | 'fallback', available: boolean } {
        const available = this.checkLocalStorageAvailability();
        return {
            type: available ? 'localStorage' : 'fallback',
            available
        };
    }
}

export default LocalStorageHelper;